import React, { useEffect, useRef, useState, useCallback } from 'react';
import client from '../api/client';

// Extracts the verification token from either a full URL
// (https://.../verify/<token>) or a bare token string.
function extractToken(text) {
  const match = text.match(/verify\/([a-f0-9]{16,64})/i);
  if (match) return match[1];
  if (/^[a-f0-9]{16,64}$/i.test(text.trim())) return text.trim();
  return null;
}

export default function CheckIn() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const detectorRef = useRef(null);
  const lastScanRef = useRef({ token: null, at: 0 });

  const [supported, setSupported] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [result, setResult] = useState(null); // { valid, ... } | null
  const [checking, setChecking] = useState(false);
  const [cameraError, setCameraError] = useState('');

  const runVerify = useCallback(async (token) => {
    setChecking(true);
    try {
      const { data } = await client.get(`/badges/verify/${token}`);
      setResult(data);
    } catch (err) {
      setResult({ valid: false, message: 'تعذر التحقق من البطاقة' });
    } finally {
      setChecking(false);
      setTimeout(() => setResult(null), 4000);
    }
  }, []);

  const handleDetected = useCallback((text) => {
    const token = extractToken(text);
    if (!token) return;
    const now = Date.now();
    if (lastScanRef.current.token === token && now - lastScanRef.current.at < 4000) return; // debounce repeats
    lastScanRef.current = { token, at: now };
    runVerify(token);
  }, [runVerify]);

  const stopCamera = () => {
    cancelAnimationFrame(rafRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setScanning(false);
  };

  const startCamera = async () => {
    setCameraError('');
    if (!('BarcodeDetector' in window)) {
      setSupported(false);
      return;
    }
    try {
      detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setScanning(true);
      const tick = async () => {
        if (videoRef.current && videoRef.current.readyState === 4) {
          try {
            const codes = await detectorRef.current.detect(videoRef.current);
            if (codes.length > 0) handleDetected(codes[0].rawValue);
          } catch (e) { /* ignore per-frame errors */ }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      setCameraError('تعذر الوصول للكاميرا — تأكد من منح الإذن، أو استخدم الإدخال اليدوي بالأسفل.');
    }
  };

  useEffect(() => () => stopCamera(), []);

  const submitManual = (e) => {
    e.preventDefault();
    const token = extractToken(manualInput);
    if (!token) { setResult({ valid: false, message: 'الرمز أو الرابط غير صالح' }); return; }
    runVerify(token);
    setManualInput('');
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>بوابة الدخول — التحقق من البطاقات</h2>
      <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: -10 }}>
        وجّه الكاميرا نحو رمز QR على بطاقة الاعتماد للتحقق الفوري من صلاحيتها عند بوابات الدخول.
      </p>

      <div className="grid grid-2">
        <div className="card">
          {supported ? (
            <>
              <div style={{ position: 'relative', background: '#000', borderRadius: 10, overflow: 'hidden', aspectRatio: '4/3' }}>
                <video ref={videoRef} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                {!scanning && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13 }}>الكاميرا متوقفة</div>}
              </div>
              {cameraError && <div className="alert alert-error" style={{ marginTop: 10 }}>{cameraError}</div>}
              <div style={{ marginTop: 12 }}>
                {!scanning
                  ? <button className="btn btn-primary" onClick={startCamera}>تشغيل الكاميرا</button>
                  : <button className="btn btn-outline" onClick={stopCamera}>إيقاف الكاميرا</button>}
              </div>
            </>
          ) : (
            <div className="alert alert-error">
              متصفحك الحالي لا يدعم قراءة QR بالكاميرا مباشرة. استخدم الإدخال اليدوي بجانب، أو افتح الصفحة من متصفح Chrome على أندرويد.
            </div>
          )}

          <form onSubmit={submitManual} style={{ marginTop: 18, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
            <div className="form-field">
              <label>أو أدخل رمز/رابط البطاقة يدويًا</label>
              <input value={manualInput} onChange={e => setManualInput(e.target.value)} placeholder="الصق رابط أو رمز التحقق هنا" />
            </div>
            <button className="btn btn-outline" style={{ marginTop: 10 }}>تحقق</button>
          </form>
        </div>

        <div>
          {checking && <div className="card" style={{ textAlign: 'center', color: 'var(--muted)' }}>جارٍ التحقق...</div>}
          {!checking && result && (
            <div className="card" style={{
              textAlign: 'center', padding: 30,
              background: result.valid ? '#dcfce7' : '#fee2e2',
              border: `2px solid ${result.valid ? 'var(--success)' : 'var(--danger)'}`
            }}>
              <div style={{ fontSize: 46, marginBottom: 10 }}>{result.valid ? '✅' : '⛔'}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: result.valid ? '#166534' : '#991b1b' }}>
                {result.valid ? 'بطاقة صالحة' : 'بطاقة غير صالحة'}
              </div>
              {result.full_name_ar && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 17, fontWeight: 700 }}>{result.full_name_ar}</div>
                  {result.category && <span className="badge" style={{ background: result.color_code, marginTop: 6 }}>{result.category}</span>}
                  <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 8 }}>{result.organization} · {result.event}</div>
                  <div className="mono" style={{ fontSize: 12, marginTop: 4 }}>{result.badge_number}</div>
                </div>
              )}
              {result.message && !result.full_name_ar && <div style={{ marginTop: 8, fontSize: 13 }}>{result.message}</div>}
            </div>
          )}
          {!checking && !result && (
            <div className="card" style={{ textAlign: 'center', color: 'var(--muted)', padding: 30 }}>
              نتيجة التحقق ستظهر هنا
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
